import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, User, UploadCloud, HardDrive, AppWindow, Users, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { apiGet } from "../../utils/api";
import { useTeamFilter } from "../../utils/useTeamFilter";
import "./fileUpload.css";
import "../devices/devices.css";

const LOGS_PAGE_SIZE = 25;

const FileUpload = () => {
  const navigate = useNavigate();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const { departments, getTeamName } = useTeamFilter();
  const [selectedTeam, setSelectedTeam] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [logsPage, setLogsPage] = useState(1);

  const fetchUploads = async () => {
    setLoading(true);
    try {
      const response = await apiGet("/api/file-uploads");
      if (response.ok) {
        const data = await response.json();
        setUploads(data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch file uploads:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUploads();
    // Refresh every 30 seconds
    const interval = setInterval(fetchUploads, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatFileSize = (bytes) => {
    if (!bytes) return "Unknown";
    const sizes = ["Bytes", "KB", "MB", "GB"];
    if (bytes === 0) return "0 Bytes";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // One card per device, summarizing its upload activity — mirrors the
  // agent-card grid on the File Monitor page instead of a bare dropdown.
  const getAgentSummaries = () => {
    const map = new Map();
    uploads.forEach((upload) => {
      const agentId = upload.agentId;
      if (!agentId) return;
      if (!map.has(agentId)) {
        map.set(agentId, {
          agentId,
          displayName: upload.agent || agentId,
          count: 0,
          lastUpload: null,
        });
      }
      const entry = map.get(agentId);
      entry.count += 1;
      if (!entry.lastUpload || new Date(upload.timestamp) > new Date(entry.lastUpload)) {
        entry.lastUpload = upload.timestamp;
      }
    });
    return Array.from(map.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
  };

  const agentSummaries = getAgentSummaries();
  const filteredAgentSummaries = agentSummaries
    .filter((agent) => selectedTeam === "all" || getTeamName(agent.agentId) === selectedTeam)
    .filter((agent) => agent.displayName.toLowerCase().includes(searchTerm.trim().toLowerCase()));
  const filteredUploads = selectedAgent
    ? uploads.filter((u) => u.agentId === selectedAgent)
    : [];
  const logsTotalPages = Math.max(1, Math.ceil(filteredUploads.length / LOGS_PAGE_SIZE));
  const pagedUploads = filteredUploads.slice((logsPage - 1) * LOGS_PAGE_SIZE, logsPage * LOGS_PAGE_SIZE);

  // Stats for the detail view, mirroring URL Tracker's stat-card row.
  const getStats = () => {
    const totalSize = filteredUploads.reduce((sum, u) => sum + (u.file?.size || 0), 0);
    const uniqueApps = new Set(filteredUploads.map((u) => u.app?.name).filter(Boolean)).size;
    return { totalUploads: filteredUploads.length, totalSize, uniqueApps };
  };

  if (loading) {
    return (
      <div className="file-upload-wrapper">
        <p className="loading">Loading file upload data...</p>
      </div>
    );
  }

  // Device picker — card grid view
  if (!selectedAgent) {
    return (
      <div className="file-upload-wrapper">
        <div className="file-upload-header-row">
          <p className="file-upload-subtitle">Select a device to view its file upload activity</p>
          <button onClick={fetchUploads} className="gc-icon-btn" data-tip="Refresh">
            <RefreshCw size={14} />
          </button>
        </div>

        <div className="team-filter-row">
          <div className="name-search-wrapper">
            <Search size={16} className="team-filter-icon" />
            <input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {departments.length > 0 && (
            <div className="team-select-wrapper">
              <Users size={16} className="team-filter-icon" />
              <select value={selectedTeam} onChange={(e) => setSelectedTeam(e.target.value)}>
                <option value="all">All Teams</option>
                {departments.map((dept) => (
                  <option key={dept._id} value={dept.name}>{dept.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {filteredAgentSummaries.length === 0 ? (
          <div className="empty-state">
            <UploadCloud size={48} />
            <p>No file upload events found</p>
          </div>
        ) : (
          <div className="agents-grid">
            {filteredAgentSummaries.map((agent) => (
              <div
                key={agent.agentId}
                className="agent-card"
                onClick={() => { setSelectedAgent(agent.agentId); setLogsPage(1); }}
              >
                <div className="agent-icon">
                  <User size={24} />
                </div>
                <div className="agent-info">
                  <h3>{agent.displayName}</h3>
                  <p className="agent-hostname">Last upload: {formatDate(agent.lastUpload)}</p>
                  <div className="agent-counts">
                    <span className="count-uploads">{agent.count} upload{agent.count !== 1 ? "s" : ""}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const stats = getStats();

  // Detail view — uploads table for the selected device
  return (
    <div className="file-upload-wrapper">
      <div className="file-upload-header-row">
        <button className="gc-icon-btn" onClick={() => setSelectedAgent(null)} data-tip="Back to Devices" aria-label="Back to Devices">
          <ArrowLeft size={18} />
        </button>
        <h1 className="file-upload-title">
          <UploadCloud size={24} />
          {selectedAgent}
        </h1>
        <button onClick={fetchUploads} className="gc-icon-btn" data-tip="Refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="file-upload-stats-grid">
        <div className="file-upload-stat-card">
          <div className="stat-icon uploads">
            <UploadCloud size={20} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.totalUploads}</span>
            <span className="stat-label">Total Uploads</span>
          </div>
        </div>
        <div className="file-upload-stat-card">
          <div className="stat-icon size">
            <HardDrive size={20} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{formatFileSize(stats.totalSize)}</span>
            <span className="stat-label">Total Size</span>
          </div>
        </div>
        <div className="file-upload-stat-card">
          <div className="stat-icon apps">
            <AppWindow size={20} />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.uniqueApps}</span>
            <span className="stat-label">Unique Apps</span>
          </div>
        </div>
      </div>

      <div className="file-upload-content">
        <table className="file-upload-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>User/Device</th>
              <th>File Name</th>
              <th>File Directory</th>
              <th>File Size</th>
              <th>Location</th>
            </tr>
          </thead>
          <tbody>
            {filteredUploads.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  No file upload events found
                </td>
              </tr>
            ) : (
              pagedUploads.map((upload, idx) => (
                <tr key={upload.id || idx}>
                  <td className="timestamp-cell">{formatDate(upload.timestamp)}</td>
                  <td>
                    <span className="agent-name">
                      {upload.agentId || upload.agent || "Unknown"}
                    </span>
                  </td>
                  <td>
                    <span className="file-name">{upload.file?.name || "Unknown"}</span>
                  </td>
                  <td>
                    <span className="file-path" title={upload.file?.path}>
                      {upload.file?.path
                        ? upload.file.path.substring(
                          0,
                          upload.file.path.lastIndexOf("\\") || upload.file.path.lastIndexOf("/")
                        ) || upload.file.path
                        : "Unknown"}
                    </span>
                  </td>
                  <td>{formatFileSize(upload.file?.size)}</td>
                  <td>
                    <div className="upload-location-cell">
                      <span className="upload-destination">{upload.destination || "External"}</span>
                      <span className="app-name">via {upload.app?.name || "Unknown"}</span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {filteredUploads.length > 0 && (
          <div className="devices-pagination">
            <span className="devices-pagination-count">
              {(logsPage - 1) * LOGS_PAGE_SIZE + 1}–{Math.min(logsPage * LOGS_PAGE_SIZE, filteredUploads.length)} of {filteredUploads.length} event{filteredUploads.length !== 1 ? "s" : ""}
            </span>
            <div className="devices-pagination-controls">
              <button
                className="gc-icon-btn"
                data-tip="Previous page"
                onClick={() => setLogsPage((p) => Math.max(1, p - 1))}
                disabled={logsPage <= 1}
              >
                <ChevronLeft size={15} />
              </button>
              <span className="devices-pagination-page">{logsPage} / {logsTotalPages}</span>
              <button
                className="gc-icon-btn"
                data-tip="Next page"
                onClick={() => setLogsPage((p) => Math.min(logsTotalPages, p + 1))}
                disabled={logsPage >= logsTotalPages}
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>

      <p className="file-upload-footer">
        Showing {filteredUploads.length} file upload event{filteredUploads.length !== 1 ? "s" : ""} for device: {selectedAgent}
      </p>
    </div>
  );
};

export default FileUpload;
